/**
 * social_security.ts — Social Security benefit calculations.
 *
 * Models AIME → PIA → claiming-age-adjusted monthly benefit.
 *
 * Sources:
 *  - SSA.gov OASDI bend points 2024
 *  - SSA Publication 05-10024 (early/late claiming reductions/credits)
 *
 * Simplifications:
 *  - Past earnings are NOT wage-indexed to age-60 (conservative; real benefit
 *    would be slightly higher due to wage growth exceeding inflation historically).
 *  - Uses 2024 bend points; not projected forward (bend points grow with average
 *    wages, roughly keeping pace in real terms, so this is reasonable for
 *    constant-currency planning).
 *  - SS COLA is modeled as equivalent to general inflation in the simulation loop
 *    (benefits are reported in real starting-year dollars, which holds if COLA ≈ CPI).
 */

import {
  SS_WAGE_BASE,
  SS_BEND_POINT_1,
  SS_BEND_POINT_2,
  SS_FRA_AGE,
  SS_MAX_DELAY_AGE,
  SS_DELAY_CREDIT_PER_YEAR,
  SS_FACTOR_BELOW_BEND1,
  SS_FACTOR_BETWEEN_BENDS,
  SS_FACTOR_ABOVE_BEND2,
  SS_EARLY_REDUCTION_FIRST_36,
  SS_EARLY_REDUCTION_BEYOND_36,
  SS_EARLY_REDUCTION_BREAKPOINT,
  SS_PCT_DIVISOR,
  MONTHS_PER_YEAR,
} from './defaults'

/** Number of years SS uses for AIME (top 35 earning years) */
const SS_AIME_YEARS = 35

/**
 * Compute AIME (Average Indexed Monthly Earnings).
 *
 * Takes the best SS_AIME_YEARS earning years out of the total years provided.
 * - maxContribYears: years at or above the SS wage base ceiling
 * - nearMaxContribYears: years at nearMaxFraction × SS_WAGE_BASE
 * - remaining years (to fill 35) are treated as zero earnings
 *
 * Since this model doesn't index historical wages, it conservatively uses the
 * current SS_WAGE_BASE for all max years.
 */
export function calculateAIME(
  maxContribYears: number,
  nearMaxContribYears: number,
  nearMaxFraction: number,
): number {
  const maxCapped = Math.min(maxContribYears, SS_AIME_YEARS)
  const nearMaxCapped = Math.min(nearMaxContribYears, SS_AIME_YEARS - maxCapped)

  const maxEarnings = maxCapped * SS_WAGE_BASE
  const nearMaxEarnings = nearMaxCapped * SS_WAGE_BASE * nearMaxFraction

  return (maxEarnings + nearMaxEarnings) / (SS_AIME_YEARS * MONTHS_PER_YEAR)
}

/**
 * Compute PIA (Primary Insurance Amount) from AIME using 2024 bend points.
 * PIA = monthly benefit at Full Retirement Age (FRA).
 */
export function calculatePIA(aime: number): number {
  const tier1 = Math.min(aime, SS_BEND_POINT_1) * SS_FACTOR_BELOW_BEND1
  const tier2 = Math.max(0, Math.min(aime, SS_BEND_POINT_2) - SS_BEND_POINT_1) * SS_FACTOR_BETWEEN_BENDS
  const tier3 = Math.max(0, aime - SS_BEND_POINT_2) * SS_FACTOR_ABOVE_BEND2
  return tier1 + tier2 + tier3
}

/**
 * Claiming multiplier applied to PIA based on age claimed vs FRA.
 *
 * Early (< FRA):  5/9% reduction per month for first 36 months early,
 *                 5/12% per month beyond 36 months early.
 * At FRA:         no adjustment (×1.0)
 * Late (> FRA):   +8% per year past FRA, capped at SS_MAX_DELAY_AGE (age 70).
 */
export function claimingMultiplier(claimingAge: number, fra: number = SS_FRA_AGE): number {
  const monthsDiff = Math.round((claimingAge - fra) * MONTHS_PER_YEAR)

  if (monthsDiff >= 0) {
    // Delayed retirement credits
    const delayYears = Math.min(claimingAge, SS_MAX_DELAY_AGE) - fra
    return 1 + Math.max(0, delayYears) * SS_DELAY_CREDIT_PER_YEAR
  }

  // Early claiming reduction
  const monthsEarly = -monthsDiff
  if (monthsEarly <= SS_EARLY_REDUCTION_BREAKPOINT) {
    return 1 - (monthsEarly * SS_EARLY_REDUCTION_FIRST_36) / SS_PCT_DIVISOR
  }
  return 1 - (SS_EARLY_REDUCTION_BREAKPOINT * SS_EARLY_REDUCTION_FIRST_36) / SS_PCT_DIVISOR
    - ((monthsEarly - SS_EARLY_REDUCTION_BREAKPOINT) * SS_EARLY_REDUCTION_BEYOND_36) / SS_PCT_DIVISOR
}

/**
 * Annual SS benefit in real (2024) dollars at claiming age.
 * SS COLA is handled externally by inflating this base amount with the
 * simulation's per-iteration inflation draw.
 */
export function annualSSBenefit(
  maxContribYears: number,
  nearMaxContribYears: number,
  nearMaxFraction: number,
  claimingAge: number,
): number {
  const aime = calculateAIME(maxContribYears, nearMaxContribYears, nearMaxFraction)
  const pia = calculatePIA(aime)
  const multiplier = claimingMultiplier(claimingAge)
  return pia * multiplier * MONTHS_PER_YEAR
}
